 
 
import { createSlice } from '@reduxjs/toolkit';

import { createSliceHook, makeActionCreator } from '@restore/helpers';
import {
  CloseTabAction,
  DeAttachAction,
  TabCoordinationState,
} from './tabCoordination.types';
import { RootStateWithInjection } from '@restore/store';

const initialState: TabCoordinationState = {
  isDetached: false,
  mainTab: false,
  initialized: false,
  loading: false,
  isDeactivated: false,
  mainWindowName: null,
  initializationError: null,
  reattachInProgress: false,
};

const { act, actEmpty } = makeActionCreator<TabCoordinationState>();

const propSetter = <K extends keyof TabCoordinationState>(key: K) =>
  act<TabCoordinationState[K]>(v => ({ [key]: v }));

export const tabCoordinationSlice = createSlice({
  name: 'tabCoordination',
  initialState,
  reducers: {
    setIsDetached: propSetter('isDetached'),
    setMainTab: propSetter('mainTab'),
    setIsDeactivated: propSetter('isDeactivated'),
    setLoading: propSetter('loading'),
    setInitialized: propSetter('initialized'),
    setMainWindowName: propSetter('mainWindowName'),
    setInitializationError: propSetter('initializationError'),
    setReattachInProgress: propSetter('reattachInProgress'),
    deAttach: actEmpty<DeAttachAction['payload']>(),
    spawnConnect: actEmpty(),
    storageTabClosed: actEmpty<CloseTabAction['payload']>(),
    // The main tab saw its detached window close while an Attach was waiting.
    detachedWindowGone: actEmpty<CloseTabAction['payload']>(),
  },
  selectors: {
    getTabCoordinationState: state => state,
  },
});

export const actions = { ...tabCoordinationSlice.actions };

export type TabCoordinationActions = typeof actions;

export const tabCoordinationSelector = (
  state: RootStateWithInjection<[typeof tabCoordinationSlice]>
) => {
  return state.tabCoordination ?? tabCoordinationSlice.getInitialState();
};

export const useTabCoordinationSlice = createSliceHook(
  tabCoordinationSlice.name,
  tabCoordinationSlice.actions
);
